import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import {
  Add,
  Visibility,
  Assignment,
  CheckCircle,
  Person,
} from '@mui/icons-material'
import { PageHeader, Card, Button, DataTable, Modal, StatusChip } from '@components/common'
import { useAuth } from '@context/AuthContext'
import api from '@services/api'
import { toast } from 'react-toastify'

const priorityColors = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'success',
}

const FaultReportsList = () => {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('fault_reports.manage')
  const canCreate = hasPermission('fault_reports.create')

  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [createWOModalOpen, setCreateWOModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedReport, setSelectedReport] = useState(null)
  const [users, setUsers] = useState([])
  const [assignedTo, setAssignedTo] = useState('')
  const [saving, setSaving] = useState(false)

  const fetchReports = async () => {
    try {
      setLoading(true)
      const response = await api.get('/assets/fault-reports/')
      setReports(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching fault reports:', error)
      toast.error('Failed to load fault reports')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await api.get('/common/users/')
      setUsers(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  useEffect(() => {
    fetchReports()
    fetchUsers()
  }, [])

  const handleViewDetail = (report) => {
    setSelectedReport(report)
    setDetailModalOpen(true)
  }

  const handleAcknowledge = async (report) => {
    try {
      await api.post(`/assets/fault-reports/${report.id}/acknowledge/`)
      toast.success('Fault report acknowledged')
      fetchReports()
    } catch (error) {
      toast.error('Failed to acknowledge fault report')
    }
  }

  const handleOpenAssign = (report) => {
    setSelectedReport(report)
    setAssignedTo(report.assigned_to || '')
    setAssignModalOpen(true)
  }

  const handleAssign = async () => {
    if (!assignedTo) {
      toast.error('Please select a user')
      return
    }
    setSaving(true)
    try {
      await api.post(`/assets/fault-reports/${selectedReport.id}/assign/`, {
        assigned_to: assignedTo,
      })
      toast.success('Fault report assigned')
      setAssignModalOpen(false)
      fetchReports()
    } catch (error) {
      toast.error('Failed to assign fault report')
    } finally {
      setSaving(false)
    }
  }

  const handleOpenCreateWO = (report) => {
    setSelectedReport(report)
    setCreateWOModalOpen(true)
  }

  const handleCreateWorkOrder = async () => {
    setSaving(true)
    try {
      const response = await api.post(`/assets/fault-reports/${selectedReport.id}/create_work_order/`)
      toast.success('Work order created successfully')
      setCreateWOModalOpen(false)
      fetchReports()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create work order')
    } finally {
      setSaving(false)
    }
  }

  const handleResolve = async (report) => {
    try {
      await api.post(`/assets/fault-reports/${report.id}/resolve/`)
      toast.success('Fault report resolved')
      fetchReports()
    } catch (error) {
      toast.error('Failed to resolve fault report')
    }
  }

  const columns = [
    {
      field: 'report_number',
      headerName: 'Report #',
      sortable: true,
    },
    {
      field: 'title',
      headerName: 'Title',
      sortable: true,
    },
    {
      field: 'asset_name',
      headerName: 'Asset',
      renderCell: (value, row) => (
        <Box>
          <Typography variant="body2">{value}</Typography>
          {row.asset_tag && (
            <Typography variant="caption" color="text.secondary">
              #{row.asset_tag}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      renderCell: (value) => (
        <Chip
          label={value}
          size="small"
          color={priorityColors[value] || 'default'}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (value) => <StatusChip status={value} />,
    },
    {
      field: 'reported_by_name',
      headerName: 'Reported By',
    },
    {
      field: 'assigned_to_name',
      headerName: 'Assigned To',
      renderCell: (value) => value || '-',
    },
    {
      field: 'reported_at',
      headerName: 'Reported',
      renderCell: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleViewDetail(row)
            }}
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          {canManage && row.status === 'reported' && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleAcknowledge(row)
              }}
              title="Acknowledge"
              color="primary"
            >
              <CheckCircle fontSize="small" />
            </IconButton>
          )}
          {canManage && !['resolved', 'closed', 'cancelled'].includes(row.status) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleOpenAssign(row)
              }}
              title="Assign"
              color="info"
            >
              <Person fontSize="small" />
            </IconButton>
          )}
          {canManage && !row.work_order && !['resolved', 'closed', 'cancelled'].includes(row.status) && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleOpenCreateWO(row)
              }}
              title="Create Work Order"
              color="warning"
            >
              <Assignment fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Fault Reports"
        subtitle="View and manage equipment fault reports"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fault Reports' },
        ]}
      />

      <Card>
        <DataTable
          columns={columns}
          data={reports}
          loading={loading}
          searchPlaceholder="Search fault reports..."
          emptyMessage="No fault reports found"
          onRowClick={handleViewDetail}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={`Fault Report: ${selectedReport?.report_number || ''}`}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDetailModalOpen(false)}>
              Close
            </Button>
            {canManage && selectedReport && !['resolved', 'closed', 'cancelled'].includes(selectedReport.status) && (
              <Button color="success" onClick={() => { setDetailModalOpen(false); handleResolve(selectedReport); }}>
                Resolve
              </Button>
            )}
          </>
        }
      >
        {selectedReport && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Title</Typography>
              <Typography variant="body1">{selectedReport.title}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Asset</Typography>
              <Typography variant="body1">{selectedReport.asset_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Priority</Typography>
              <Box>
                <Chip
                  label={selectedReport.priority}
                  size="small"
                  color={priorityColors[selectedReport.priority] || 'default'}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <StatusChip status={selectedReport.status} />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body1">{selectedReport.description}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Reported By</Typography>
              <Typography variant="body1">{selectedReport.reported_by_name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Reported At</Typography>
              <Typography variant="body1">
                {new Date(selectedReport.reported_at).toLocaleString()}
              </Typography>
            </Grid>
            {selectedReport.assigned_to_name && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography variant="body1">{selectedReport.assigned_to_name}</Typography>
              </Grid>
            )}
            {selectedReport.work_order && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Work Order</Typography>
                <Typography variant="body1">{selectedReport.work_order_number || `WO #${selectedReport.work_order}`}</Typography>
              </Grid>
            )}
            {selectedReport.resolution_notes && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Resolution Notes</Typography>
                <Typography variant="body1">{selectedReport.resolution_notes}</Typography>
              </Grid>
            )}
            {/* Images */}
            {(selectedReport.image_1 || selectedReport.image_2 || selectedReport.image_3 || selectedReport.image_4) && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Images</Typography>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  {[selectedReport.image_1, selectedReport.image_2, selectedReport.image_3, selectedReport.image_4]
                    .filter(Boolean)
                    .map((img, idx) => (
                      <Box
                        key={idx}
                        component="img"
                        src={img}
                        alt={`Fault image ${idx + 1}`}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(img, '_blank')}
                      />
                    ))}
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>

      {/* Assign Modal */}
      <Modal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title="Assign Fault Report"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} loading={saving}>
              Assign
            </Button>
          </>
        }
      >
        <FormControl fullWidth>
          <InputLabel>Assign To</InputLabel>
          <Select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            label="Assign To"
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Modal>

      {/* Create Work Order Modal */}
      <Modal
        open={createWOModalOpen}
        onClose={() => setCreateWOModalOpen(false)}
        title="Create Work Order"
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setCreateWOModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorkOrder} loading={saving}>
              Create Work Order
            </Button>
          </>
        }
      >
        <Typography>
          Are you sure you want to create a work order from this fault report?
        </Typography>
        {selectedReport && (
          <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
            <Typography variant="body2">
              <strong>Fault:</strong> {selectedReport.title}
            </Typography>
            <Typography variant="body2">
              <strong>Asset:</strong> {selectedReport.asset_name}
            </Typography>
            <Typography variant="body2">
              <strong>Priority:</strong> {selectedReport.priority}
            </Typography>
          </Box>
        )}
      </Modal>
    </Box>
  )
}

export default FaultReportsList
